import { useMemo } from "react";

/**
 * StarConstellation — ambient drifting brass stars.
 *
 * Renders a deterministic (seeded) field of very low-opacity 5-point stars
 * positioned across the container. Each star drifts + rotates on its own
 * slow loop. Absolute-positioned; intended to sit behind content as an
 * atmospheric texture layer. Respects prefers-reduced-motion.
 *
 * Usage:
 *   <div className="relative">
 *     <StarConstellation count={18} seed={7} />
 *     ... content ...
 *   </div>
 */
export default function StarConstellation({
  count = 16,
  seed = 42,
  className = "",
  minSize = 8,
  maxSize = 22,
  color = "#9D7E4C",
  maxOpacity = 0.10,
}) {
  const stars = useMemo(() => {
    // Deterministic pseudo-random from a seed — stars stay put across renders
    let s = seed;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    return Array.from({ length: count }, (_, i) => {
      const size = minSize + rand() * (maxSize - minSize);
      return {
        id: i,
        top: `${(rand() * 100).toFixed(2)}%`,
        left: `${(rand() * 100).toFixed(2)}%`,
        size,
        opacity: 0.04 + rand() * (maxOpacity - 0.04),
        rotate: rand() * 360,
        driftX: (rand() - 0.5) * 30,    // px drift on the X axis
        driftY: (rand() - 0.5) * 20,    // px drift on the Y axis
        duration: 26 + rand() * 34,     // 26-60s per cycle
        delay: -rand() * 40,            // negative delay so they're offset from start
      };
    });
  }, [count, seed, minSize, maxSize, maxOpacity]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
      data-testid="star-constellation"
    >
      {stars.map((star) => (
        <svg
          key={star.id}
          width={star.size}
          height={star.size}
          viewBox="0 0 100 100"
          className="lsc-const-star"
          style={{
            position: "absolute",
            top: star.top,
            left: star.left,
            opacity: star.opacity,
            fill: color,
            "--drift-x": `${star.driftX}px`,
            "--drift-y": `${star.driftY}px`,
            "--rot-start": `${star.rotate}deg`,
            "--rot-end": `${star.rotate + 15}deg`,
            animation: `lsc-const-drift ${star.duration.toFixed(1)}s ease-in-out ${star.delay.toFixed(1)}s infinite alternate`,
          }}
        >
          <polygon points={STAR_POINTS} />
        </svg>
      ))}
      <style>{`
        @keyframes lsc-const-drift {
          0%   { transform: translate(0, 0) rotate(var(--rot-start)); }
          100% { transform: translate(var(--drift-x), var(--drift-y)) rotate(var(--rot-end)); }
        }
        @media (prefers-reduced-motion: reduce) {
          .lsc-const-star { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

// Precomputed 5-point star polygon (cx=50, cy=50, rOuter=48, rInner=20)
const STAR_POINTS = (() => {
  const cx = 50, cy = 50, rOuter = 48, rInner = 20;
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
})();
